import React from 'react';
import i18n from 'i18next';
import { css } from '@emotion/core';
import Typography from '@material-ui/core/Typography';
import { setTextVariantStyle } from 'utils/variantStyle';
import { entryVerifyStatus } from 'config/entryVerifyStatus';
import getUrlWrapTestCode from 'ugc/utils/getUrlWrapTestCode';
import { verifyPass } from 'Mui/styles/color';
import {
    isShowResultDownloadBtnGame,
    isShowUgcResultReuploadBtnGame
} from 'config/promoGameTypesClientConfig';

import {
    usePrimaryPromoSelector,
    useSecondaryPromoSelector
} from 'client/game_photoContest/hooks/useReduxSelector';
import useIsContestUgcModule from 'client/game_photoContest/hooks/useIsContestUgcModule';
import UgcReuploadBtn from 'client/components/Result/EntriesInfo/customResult/UgcReuploadBtn';
import ResultDownloadBtn from 'client/components/Result/EntriesInfo/customResult/ResultDownloadBtn';
import VerifyStatusLabel from 'client/components/verify/VerifyStatusLabel';
import UgcVoteInfo from 'client/components/Result/EntriesInfo/customResult/UgcVoteInfo';
import ResponsiveUgcProxyPicture from 'client/components/ResponsiveUgcProxyPicture';
import { gameTypes } from 'config/promoGameTypes';
import ugcTypes from 'config/ugcTypes';
import VideoPlayer from 'components/VideoPlayer';

const proxyConfig = {
    normal: { resizeWidth: 720 }
};

const StatusLabel = ({ validStatus, isContestUgcModule, mediaLibraryUuid, componentStyle }) => {
    if (validStatus === entryVerifyStatus.PASSED) {
        if (isContestUgcModule) {
            return (
                <UgcVoteInfo mediaLibraryUUID={mediaLibraryUuid} componentStyle={componentStyle} />
            );
        }
        return (
            <div css={statusStyle}>
                <Typography variant="subtitle2">{i18n.t('label.passed')}</Typography>
            </div>
        );
    }
    return <VerifyStatusLabel status={validStatus} />;
};

const DateDisplay = ({ date, componentStyle }) => {
    return (
        <Typography variant="subtitle2" css={dateStyle(componentStyle?.titleColor)}>
            {i18n.t('label.date.value', { date })}
        </Typography>
    );
};

const MediaDisplay = ({ media, isVideoUpload, title, breakpointConfigs, wrapperStyle }) => {
    if (isVideoUpload) {
        return (
            <div css={wrapperStyle}>
                <VideoPlayer url={media.result} />
            </div>
        );
    }

    return (
        <div css={wrapperStyle}>
            <ResponsiveUgcProxyPicture
                src={media.result}
                alt={title}
                breakPointConfigs={breakpointConfigs}
                enableLazyLoad
                css={ugcImageStyle}
            />
        </div>
    );
};

const ActionButtons = ({
    media,
    validStatus,
    showResultDownloadButton,
    showUgcReuploadBtn,
    componentStyle,
    onReuploadBtnClick
}) => {
    const shouldShowButtons =
        showResultDownloadButton ||
        (showUgcReuploadBtn && validStatus === entryVerifyStatus.NOT_PASS);

    if (!shouldShowButtons) return null;

    return (
        <div css={btnWrapperStyle}>
            {showResultDownloadButton && (
                <ResultDownloadBtn href={media.result} componentStyle={componentStyle} />
            )}
            {showUgcReuploadBtn && validStatus === entryVerifyStatus.NOT_PASS && (
                <UgcReuploadBtn
                    style={reuploadBtnStyle}
                    componentStyle={componentStyle}
                    onClick={onReuploadBtnClick}
                />
            )}
        </div>
    );
};

const WorkPageLink = ({ ugcUrl, title, componentStyle, style }) => (
    <div css={[btnConatinerStyle, style]}>
        <a
            css={linkStyle(componentStyle)}
            alt={title}
            target="_blank"
            rel="noopener noreferrer"
            href={ugcUrl}
        >
            {i18n.t('button.go_to_work_page')}
        </a>
    </div>
);

const SingleItemView = ({
    mediaData,
    resultCreatedAt,
    title,
    ugcUrl,
    componentStyle,
    validStatus,
    isContestUgcModule,
    mediaLibraryUuid,
    onReuploadBtnClick,
    renderCustomBtnComponent,
    showResultDownloadButton,
    showUgcReuploadBtn,
    isVideoUpload
}) => {
    const shouldShowDateStatusRow = !isContestUgcModule || validStatus !== entryVerifyStatus.PASSED;

    return (
        <div css={singleItemLayoutStyle}>
            <div css={singleItemInfoStyle}>
                {shouldShowDateStatusRow ? (
                    <div css={dateStatusRowStyle}>
                        <DateDisplay date={resultCreatedAt} componentStyle={componentStyle} />
                        <StatusLabel
                            validStatus={validStatus}
                            isContestUgcModule={isContestUgcModule}
                            mediaLibraryUuid={mediaLibraryUuid}
                            componentStyle={componentStyle}
                        />
                    </div>
                ) : (
                    <>
                        <DateDisplay date={resultCreatedAt} componentStyle={componentStyle} />
                        <StatusLabel
                            validStatus={validStatus}
                            isContestUgcModule={isContestUgcModule}
                            mediaLibraryUuid={mediaLibraryUuid}
                            componentStyle={componentStyle}
                        />
                    </>
                )}
                {renderCustomBtnComponent()}
            </div>

            {mediaData.length > 0 && (
                <>
                    <MediaDisplay
                        media={mediaData[0]}
                        isVideoUpload={isVideoUpload}
                        title={title}
                        breakpointConfigs={proxyConfig}
                        wrapperStyle={
                            isVideoUpload ? singleVideoWrapperStyle : singleImageWrapperStyle
                        }
                    />
                    <ActionButtons
                        media={mediaData[0]}
                        validStatus={validStatus}
                        showResultDownloadButton={showResultDownloadButton}
                        showUgcReuploadBtn={showUgcReuploadBtn}
                        componentStyle={componentStyle}
                        onReuploadBtnClick={onReuploadBtnClick}
                    />
                </>
            )}

            <WorkPageLink
                ugcUrl={ugcUrl}
                title={title}
                componentStyle={componentStyle}
                style={singleWorkPageLinkStyle}
            />
        </div>
    );
};

const MultipleItemsView = ({
    mediaData,
    resultCreatedAt,
    title,
    ugcUrl,
    componentStyle,
    validStatus,
    isContestUgcModule,
    mediaLibraryUuid,
    onReuploadBtnClick,
    renderCustomBtnComponent,
    showResultDownloadButton,
    showUgcReuploadBtn,
    isVideoUpload
}) => {
    return (
        <div css={ugcCardStyle}>
            <div css={mediaWrapperStyle}>
                {mediaData.map(({ result, id: ugcId }) => (
                    <div key={ugcId} css={mediaItemStyle}>
                        <MediaDisplay
                            media={{ result }}
                            isVideoUpload={isVideoUpload}
                            title={title}
                            breakpointConfigs={proxyConfig}
                            wrapperStyle={
                                isVideoUpload
                                    ? multipleVideoWrapperStyle
                                    : multipleImageWrapperStyle
                            }
                        />
                        <ActionButtons
                            media={{ result }}
                            validStatus={validStatus}
                            showResultDownloadButton={showResultDownloadButton}
                            showUgcReuploadBtn={showUgcReuploadBtn}
                            componentStyle={componentStyle}
                            onReuploadBtnClick={onReuploadBtnClick}
                        />
                    </div>
                ))}
            </div>
            <div css={ugcInfoStyle}>
                <DateDisplay date={resultCreatedAt} componentStyle={componentStyle} />
                <StatusLabel
                    validStatus={validStatus}
                    isContestUgcModule={isContestUgcModule}
                    mediaLibraryUuid={mediaLibraryUuid}
                    componentStyle={componentStyle}
                />
                {renderCustomBtnComponent()}
                <WorkPageLink ugcUrl={ugcUrl} title={title} componentStyle={componentStyle} />
            </div>
        </div>
    );
};

const ResultUGC = ({
    isMultiple,
    serialNo,
    resultCreatedAt,
    componentStyle,
    blockTitle,
    validStatus,
    resultInfo: { mediaLibraryUuid, title, media } = {},
    onReuploadBtnClick = () => {},
    renderCustomBtnComponent = () => false
}) => {
    const isContestUgcModule = useIsContestUgcModule();
    const { id: promoId, type: gameType } = usePrimaryPromoSelector(s => s.promo);
    const { type: secondaryGameType } = useSecondaryPromoSelector(s => s.promo);

    const ugcUrl = getUrlWrapTestCode(`${window.location.origin}/ugc/${promoId}/${serialNo}`);
    const showResultDownloadButton = isShowResultDownloadBtnGame(gameType);
    const showUgcReuploadBtn = isShowUgcResultReuploadBtnGame(gameType);
    const isVideoUpload = secondaryGameType === gameTypes.VIDEO_UPLOAD;

    const mediaData = React.useMemo(() => {
        return (media || []).filter(item => {
            const targetType = isVideoUpload ? ugcTypes.VIDEO : ugcTypes.IMAGE;
            return item.type === targetType;
        });
    }, [media, isVideoUpload]);

    const commonProps = {
        mediaData,
        resultCreatedAt,
        title,
        ugcUrl,
        componentStyle,
        validStatus,
        isContestUgcModule,
        mediaLibraryUuid,
        onReuploadBtnClick,
        renderCustomBtnComponent,
        showResultDownloadButton,
        showUgcReuploadBtn,
        isVideoUpload
    };

    return (
        <div css={containerStyle}>
            {Boolean(blockTitle) && (
                <Typography variant="h6" css={[blockTitleStyle, titleStyle(componentStyle)]}>
                    {blockTitle}
                </Typography>
            )}
            {isMultiple ? (
                <MultipleItemsView {...commonProps} />
            ) : (
                <SingleItemView {...commonProps} />
            )}
        </div>
    );
};

export default ResultUGC;

const statusStyle = css`
    width: fit-content;
    background-color: ${verifyPass.color};
    padding: 2px 6px;
    border-radius: 4px;
    color: #fff;
    > * {
        color: inherit;
    }
`;

const containerStyle = css`
    width: 100%;
`;

const blockTitleStyle = css`
    margin-top: 8px;
`;

const mediaItemStyle = css`
    &:not(:last-child) {
        margin-bottom: 16px;
    }
`;

const dateStatusRowStyle = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

const titleStyle = (componentStyle = {}) => css`
    font-weight: 500;
    color: ${componentStyle.titleColor};
    word-break: break-all;
`;

const linkStyle = (componentStyle = {}) => css`
    text-decoration: underline;
    color: ${componentStyle.buttonBgColor};
    margin-right: 16px;
    cursor: pointer;
    letter-spacing: 1.25px;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
`;

const btnWrapperStyle = css`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
`;

const reuploadBtnStyle = css`
    margin-left: 16px;
`;

const singleVideoWrapperStyle = css`
    width: 100%;
    aspect-ratio: 1/1;
    position: relative;
    overflow: hidden;
`;

const multipleImageWrapperStyle = css`
    width: 80px;
    height: 80px;
    position: relative;
    overflow: hidden;
`;

const multipleVideoWrapperStyle = css`
    height: 80px;
    width: 128px;
    position: relative;
    overflow: hidden;
`;

const ugcImageStyle = css`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ugcCardStyle = css`
    display: flex;
    align-items: flex-start;
    padding: 8px 0px;
`;

const mediaWrapperStyle = css`
    flex-shrink: 0;
    margin-right: 8px;
`;

const ugcInfoStyle = css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 80px;
`;

const btnConatinerStyle = css`
    display: flex;
    ${setTextVariantStyle('button')}
    & button {
        color: inherit;
    }
`;

const singleItemLayoutStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const singleImageWrapperStyle = css`
    width: 100%;
    aspect-ratio: 1/1;
    position: relative;
    overflow: hidden;
`;

const singleItemInfoStyle = css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8.5px;
    margin-bottom: 8px;
`;

const singleWorkPageLinkStyle = css`
    padding: 16px 0px 8px 0px;
`;

const dateStyle = titleColor => css`
    color: ${titleColor};
`;
